import _ from "lodash";
import classNames from "classnames";

export interface Select4Item<K extends string = string> {
  label: string;
  key: K;
}

export type Select4Props<K extends string> =
  | Select4WithNullValue<K>
  | Select4WithDefaultValue<K>;

// Case where you want something to be displayed when nothing has been chosen (key = null)
interface Select4WithNullValue<K extends string> {
  items: Select4Item<K>[];
  onSelect: (key: K | null) => void;
  nullDisplay: string;
  selected: K | null;
  className: string;
  disabled?: boolean;
  testid?: string;
  id?: string;
  width?: string;
  smallHeight?: boolean;
  sort?: boolean;
}

// Case where you want a default key to be selected (you can't select nothing)
interface Select4WithDefaultValue<K extends string> {
  items: Select4Item<K>[];
  onSelect: (key: K) => void;
  defaultKey: K;
  selected: K;
  className: string;
  disabled?: boolean;
  testid?: string;
  id?: string;
  width?: string;
  smallHeight?: boolean;
  sort?: boolean;
}

const Select4 = <K extends string>(props: Select4Props<K>) => {
  const style = props.width ? { width: props.width } : {};

  const handleChange = (key: string) => {
    if ("nullDisplay" in props && key === props.nullDisplay) {
      props.onSelect(null);
    } else {
      props.onSelect(key as K);
    }
  };

  const sortedItems = props.sort
    ? _.sortBy(props.items, (item) => item.label)
    : props.items;

  return (
    <select
      onChange={(event) => handleChange(event.target.value)}
      className={classNames(
        props.className,
        "border rounded-md p-2 border-slate-300 border-solid bg-white transition-colors focus:outline-none focus:border-blue-700 focus:transition-all focus:shadow-blue-700",
        {
          ["h-8"]: props.smallHeight,
          ["h-10"]: !props.smallHeight,
        }
      )}
      value={
        props.selected !== null
          ? props.selected
          : "defaultKey" in props
            ? props.defaultKey
            : props.nullDisplay
      }
      disabled={props.disabled}
      data-testid={props.testid || ""}
      id={props.id || ""}
      style={style}
    >
      {"nullDisplay" in props ? (
        <option value={props.nullDisplay} key={"emptyKey"}>
          {props.nullDisplay}
        </option>
      ) : null}
      {sortedItems.map((item) => (
        <option value={item.key} key={item.key}>
          {item.label}
        </option>
      ))}
    </select>
  );
};

export default Select4;
